import {Deferred, EOP_ERRORS, schedule} from "../../../common/utils/promises";
import {LoadAfterConfirmationEvents} from "../../../page/elements/loadAfterConfirmation";
import {IntersectionObserverFactory, onceIntersected, ResizeObserverFactory} from "../../../common/observation";
import {resolve} from "../../../container";
import {YoutubeIframeApiLoader, YoutubeService, YoutubeVideoProgress} from "./youtube";
import {UnLitElement} from "../../../common/elements";
import {property} from "lit/decorators.js";
import {type VideoTracking, VideoTrackingFactory} from "../../../tracking/videoTracking";

const YOUTUBE_NOCOOKIE_HOST = "https://www.youtube-nocookie.com";

export class EopYoutubePlayer extends UnLitElement {

    @property({attribute: "aspect-ratio"})
    public aspectRatio: string;

    private player: YT.Player;
    private intersectionObserver: IntersectionObserver;
    private intersectionDeferred: Deferred<void>;
    private playerReadyDeferred: Deferred<void>;

    private youtubeIframeApiLoader: YoutubeIframeApiLoader;
    private youtubeService: YoutubeService;
    private loadAfterConfirmationEvents: LoadAfterConfirmationEvents;
    private intersectionObserverFactory: IntersectionObserverFactory;
    private resizeObserverFactory: ResizeObserverFactory;
    private videoTrackingFactory: VideoTrackingFactory;
    private videoTracking: VideoTracking;

    public constructor() {
        super();
        this.youtubeIframeApiLoader = resolve(YoutubeIframeApiLoader);
        this.youtubeService = resolve(YoutubeService);
        this.loadAfterConfirmationEvents = resolve(LoadAfterConfirmationEvents);
        this.intersectionObserverFactory = resolve(IntersectionObserverFactory);
        this.resizeObserverFactory = resolve(ResizeObserverFactory);
        this.videoTrackingFactory = resolve(VideoTrackingFactory);

        this.intersectionDeferred = new Deferred<void>();
        this.playerReadyDeferred = new Deferred<void>();
    }

    public connectedCallback(): void {
        this.intersectionObserver = this.intersectionObserverFactory.create(onceIntersected(
                () => this.intersectionDeferred.resolve()),
            {threshold: 0, rootMargin: "100% 0% 100% 0%"}
        );
        this.intersectionObserver.observe(this);

        this.loadAfterConfirmationEvents.onConfirmation("youtube", () => {

            return schedule(this.intersectionDeferred.promise.then(async () => {
                await this.youtubeIframeApiLoader.run();
                let dimensions = this.calculateDimensions();
                this.player = new YT.Player(this.querySelector(".video-placeholder")!, {
                    host: YOUTUBE_NOCOOKIE_HOST,
                    videoId: this.getAttribute("video-id") ?? "",
                    events: {
                        onStateChange: (ev) => this.onStateChange(ev),
                        onReady: () => {
                            this.playerReadyDeferred.resolve();
                        }
                    },
                    width: dimensions.width,
                    height: dimensions.height,
                    playerVars: {
                        iv_load_policy: 3,
                        rel: 0,
                        origin: this.getAttribute("origin-stage") ?? undefined,
                        enablejsapi: 1
                    }
                });
                this.youtubeService.getVideoInfo(this.getAttribute("video-id") ?? "")
                    .then(info => {
                        this.videoTracking = this.videoTrackingFactory.create(info.title ?? "", new YoutubeVideoProgress(this.player));
                    })
                    .catch(EOP_ERRORS);
                this.resizeObserverFactory.create(() => {
                    dimensions = this.calculateDimensions();
                    this.player.setSize(dimensions.width, dimensions.height);
                }).observe(document.body);
            }).catch(EOP_ERRORS)).as("iframe-api-loaded");
        });
    }

    public disconnectedCallback(): void {
        if (this.videoTracking) {
            this.videoTracking.disconnect();
        }
        this.intersectionObserver.disconnect();
    }

    public playVideo(): void {
        this.playerReadyDeferred.promise.then(() => this.player.playVideo());
    }

    public pauseVideo(): void {
        this.playerReadyDeferred.promise.then(() => this.player.pauseVideo());
    }

    private onStateChange(event: YT.OnStateChangeEvent): void {
        if (!this.videoTracking) {
            return;
        }
        const playerState = event.data;
        if (playerState === YT.PlayerState.PLAYING) {
            this.videoTracking.playing();
        }
        if (playerState === YT.PlayerState.PAUSED) {
            this.videoTracking.pause();
        }
        if (playerState === YT.PlayerState.ENDED) {
            this.videoTracking.ended();
        }
    };

    private calculateDimensions(): { height: number, width: number } {
        let height;
        let width = this.findNearestParentWithWidth(this);
        if (this.aspectRatio === "vertical") {
            const viewportHeight = window.innerHeight;
            const ratio = viewportHeight / width;
            if (ratio > 1.77) {
                height = width * 1.77;
            } else {
                height = viewportHeight;
                width = viewportHeight / 1.77;
            }
            return {height: height, width: width};
        } else {
            height = width / 1.77;
            return {height: height, width: width};
        }
    }

    private findNearestParentWithWidth(element: HTMLElement): number {
        return element.parentElement?.classList.contains("youtube-container") ? element.parentElement.offsetWidth : this.findNearestParentWithWidth(element.parentElement!);
    }
}